import { useCallback, useEffect, useRef, useState } from "react";
import {
  useBlocker,
  useNavigate,
  type BlockerFunction,
} from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import type { StockAvailabilityResponseDTO } from "@/features/stocks/types/stock.types";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { RentalType } from "@/shared/types/enums";
import { ROUTES } from "@/constants/routes";
import {
  rentalCreateSchema,
  type RentalCreateFormSchema,
} from "@/features/rentals/components/rental-create/rental-create-schema";
import { RentalAdminCreateWizard } from "@/features/rentals/admin/components/create-wizard/RentalAdminCreateWizard";
import { useRentalWizardSearch } from "@/features/rentals/admin/hooks/useRentalWizardData";
import { buildAdminCreatePayload } from "@/features/rentals/admin/utils/rental-admin-payload";
import { rentalAdminService } from "@/features/rentals/admin";

const DEFAULT_VALUES: RentalCreateFormSchema = {
  rentalType: RentalType.SUIT,
  sellerId: null,
  hasShoes: false,
  shoesPairQuantity: 0,
  totalValue: 0,
  payments: [],
  expectedOutAt: "",
  expectedInAt: "",
  description: "",
  emitInvoice: true,
  invoiceObservation: "",
  isRetroactive: false,
  outAt: "",
  inAt: "",
  selectedClientId: null,
  newClient: null,
  selectedContractId: null,
  newContract: null,
  parentContractId: null,
  stockIds: [],
  gownItems: [],
  tagIds: [],
};

/**
 * Criação de aluguel com escopo ADMIN — `/alugueis/novo-admin`.
 *
 * Espelha a `NovoAluguelPage` do USER: mesmas 6 etapas, mesmo layout e as
 * mesmas etapas de Tipo, Peças, Pagamento e Revisão, literalmente os mesmos
 * componentes. Difere no serviço (`/api/admin/rentals`), no seletor de
 * contrato (busca admin) e nas datas sem hora.
 *
 * A edição de aluguel continua no dialog da `AlugueisPage`.
 */
export function NovoAluguelAdminPage() {
  const navigate = useNavigate();
  const wizardData = useRentalWizardSearch();

  const form = useForm<RentalCreateFormSchema>({
    resolver: zodResolver(rentalCreateSchema),
    defaultValues: DEFAULT_VALUES,
    mode: "onChange",
  });

  const rentalType = form.watch("rentalType");

  const [stockItems, setStockItems] = useState<StockAvailabilityResponseDTO[]>(
    [],
  );
  const [sellerLabel, setSellerLabel] = useState<string | null>(null);
  const [contractLabel, setContractLabel] = useState("");
  const [clientLabel, setClientLabel] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isDirty = form.formState.isDirty;

  // Lido no momento em que o blocker decide — um estado não serviria: o
  // navigate do submit acontece antes do re-render que o desativaria.
  const bypassBlocker = useRef(false);

  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      isDirty &&
      !bypassBlocker.current &&
      currentLocation.pathname !== nextLocation.pathname,
    [isDirty],
  );
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const handleTypeChange = useCallback(
    (next: RentalType.SUIT | RentalType.GOWN) => {
      if (next === rentalType) return;
      form.setValue("rentalType", next, { shouldDirty: true });
      if (next === RentalType.GOWN) {
        form.setValue("stockIds", [], { shouldDirty: true });
        form.setValue("hasShoes", false, { shouldDirty: true });
        form.setValue("shoesPairQuantity", 0, { shouldDirty: true });
        setStockItems([]);
      } else {
        form.setValue("gownItems", [], { shouldDirty: true });
      }
    },
    [form, rentalType],
  );

  const handleSellerChange = useCallback(
    (sellerId: number | null, sellerName: string | null) => {
      form.setValue("sellerId", sellerId, { shouldDirty: true });
      setSellerLabel(sellerName);
    },
    [form],
  );

  const handleContractSelect = useCallback(
    (id: number, display: string, client: { id: number; name: string }) => {
      form.setValue("selectedContractId", id, { shouldDirty: true });
      setContractLabel(display);
      // O contrato carrega o cliente. Preencher `selectedClientId` satisfaz a
      // validação compartilhada do schema, que o fluxo do vendedor exige.
      form.setValue("selectedClientId", client.id, { shouldDirty: true });
      setClientLabel(client.name);
    },
    [form],
  );

  const handleContractClear = useCallback(() => {
    form.setValue("selectedContractId", null, { shouldDirty: true });
    form.setValue("selectedClientId", null, { shouldDirty: true });
    setContractLabel("");
    setClientLabel(null);
  }, [form]);

  const handleStockAdd = useCallback(
    (item: StockAvailabilityResponseDTO) => {
      const ids = form.getValues("stockIds");
      form.setValue("stockIds", [...ids, item.stockId], { shouldDirty: true });
      setStockItems((prev) => [...prev, item]);
    },
    [form],
  );

  const handleStockRemove = useCallback(
    (stockId: number) => {
      const ids = form.getValues("stockIds");
      form.setValue(
        "stockIds",
        ids.filter((id) => id !== stockId),
        { shouldDirty: true },
      );
      setStockItems((prev) => prev.filter((item) => item.stockId !== stockId));
    },
    [form],
  );

  const handleSubmit = useCallback(async () => {
    // Valida só os campos que existem nesta tela. O schema é compartilhado
    // com o fluxo do vendedor, que tem campos (contrato pai, cadastro de
    // cliente inline) sem equivalente aqui.
    const valid = await form.trigger([
      "sellerId",
      "selectedContractId",
      "selectedClientId",
      "expectedOutAt",
      "expectedInAt",
      "shoesPairQuantity",
      "totalValue",
      "description",
      "invoiceObservation",
      "outAt",
      "inAt",
    ]);
    if (!valid) {
      toast.error("Verifique os campos obrigatórios");
      return;
    }
    const data = form.getValues();
    if (!data.selectedContractId || !data.sellerId) {
      toast.error("Selecione o contrato e o vendedor");
      return;
    }
    // O backend exige paidValue > 0 em cada pagamento — barra aqui em vez de
    // deixar o 400 estourar (mesmo critério do dialog de edição).
    if (data.payments.some((p) => p.paidValue <= 0)) {
      toast.error("Informe o valor de cada pagamento antes de salvar");
      return;
    }

    setSubmitting(true);
    try {
      await rentalAdminService.create(
        buildAdminCreatePayload(data, data.selectedContractId, data.sellerId),
      );
      bypassBlocker.current = true;
      toast.success("Aluguel criado com sucesso!");
      navigate(ROUTES.RENTALS);
    } catch (error: unknown) {
      // Só o erro reabilita o botão: no sucesso a navegação desmonta a tela.
      setSubmitting(false);
      toast.error(getApiErrorMessage(error, "Erro ao criar aluguel"));
    }
  }, [form, navigate]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Novo Aluguel</h1>
        <p className="text-muted-foreground">
          Preencha as etapas abaixo. As peças são opcionais — você pode salvar o
          aluguel incompleto e completá-lo depois.
        </p>
      </div>

      <RentalAdminCreateWizard
        form={form}
        stockItems={stockItems}
        sellerLabel={sellerLabel}
        selectedContractLabel={contractLabel}
        selectedClientLabel={clientLabel}
        isSubmitting={submitting}
        onTypeChange={handleTypeChange}
        onSellerChange={handleSellerChange}
        onContractSelect={handleContractSelect}
        onContractClear={handleContractClear}
        searchContracts={wizardData.searchContracts}
        createContract={wizardData.createContract}
        searchClients={wizardData.searchClients}
        createClient={wizardData.createClient}
        onStockAdd={handleStockAdd}
        onStockRemove={handleStockRemove}
        onSubmit={handleSubmit}
      />

      <Dialog
        open={blocker.state === "blocked"}
        onOpenChange={(value) => {
          if (!value && blocker.state === "blocked") blocker.reset();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Sair sem salvar?</DialogTitle>
            <DialogDescription>
              Você tem dados preenchidos que serão perdidos se sair desta tela
              agora.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                blocker.state === "blocked" ? blocker.reset() : null
              }
            >
              Continuar editando
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() =>
                blocker.state === "blocked" ? blocker.proceed() : null
              }
            >
              Sair sem salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
